import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Container, Grid, Message} from 'semantic-ui-react';
import {hasAccessPermission} from '../../../common/permissions/validations';
import {scopes, resources} from '../../../common/permissions/dictionary';
import {paths} from '../../../common/dictionaries/urls';
import {Actions} from '../../../common/actions';
import CalendarHeader from './componentecalendario/CalendarHeader';
import CalendarFilters from './componentecalendario/CalendarFilters';
import CalendarGrid from './componentecalendario/CalendarGrid';
import CalendarSidebar from './componentecalendario/CalendarSidebar';
import WtsAgendaModal from './componenteModalAgenda/index';
import WtsAgendaViewOnly from './componenteMatricula/index';
import WtsMatriculaIndex from './componenteRevisarMatriculados';
import moment from 'moment';
import 'moment/locale/es';
import './styles.scss';

const {
  getWtsAgendas, navigateTo, getWtsAreas, changeWtsAreaSelected,
  getWtsCoursesStatus, getWtsEmployeesByCharge, getWtsDealer, getWtsCourses,
  getWtsEmployeeByEmail,
} = Actions;

const COLORS = [
  '#2185d0', '#21ba45', '#f2711c', '#fbbd08', '#e03997',
  '#a333c8', '#00b5ad', '#b5cc18', '#767676', '#000',
];

const getJWTPayload = (token) => {
  try {
    if (!token) {
      return null;
    }
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window.atob(base64)
        .split('')
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};
// eslint-disable-next-line max-statements
const WtsCalendario = () => {
  const dispatch = useDispatch();
  const session = useSelector((state) => state.session);
  const employeeSelected = useSelector((state) => state.wtsEmployees?.employeeSelected);
  const permissions = useSelector((state) => state.session?.permissions);
  const {areas, areaSelected} = useSelector((state) => state.wtsAreas || {});
  const statusRaw = useSelector((state) => state.wtsCoursesStatus?.list || []);
  const agendas = useSelector((state) => state.wtsAgendas?.list || []);
  const employees = useSelector((state) => state.wtsEmployees?.employeesByCharge || []);
  const dealersRaw = useSelector((state) => state.wtsDealer?.dealers || []);
  const coursesRaw = useSelector((state) => state.wtsCourses?.courses || []);

  const [currentYear, setCurrentYear] = useState(moment().year());
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [courseToEdit, setCourseToEdit] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [filters, setFilters] = useState({
    'status': null, 'instructor': null, 'dealer': null, 'course': null,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showMessage, setShowMessage] = useState(false);

  const canViewCalendario = useMemo(() => (
    hasAccessPermission(permissions, resources.WtsCalendario, scopes.See)
  ), [permissions]);

  const canEditCalendario = useMemo(() => (
    hasAccessPermission(permissions, resources.WtsCalendario, scopes.Edit) ||
    hasAccessPermission(permissions, resources.WtsCalendario, scopes.Create)
  ), [permissions]);

  const canViewMatricula = useMemo(() => (
    hasAccessPermission(permissions, resources.WtsCalendario, scopes.WtsMatricula) ||
    hasAccessPermission(permissions, resources.WtsMatricula, scopes.WtsMatricula)
  ), [permissions]);

  const isMatriculaUser = canViewMatricula && !canEditCalendario;
  const canEdit = canEditCalendario;
  const canSeePage = canViewCalendario || canViewMatricula;

  useEffect(() => {
    if (!canSeePage && session) {
      dispatch(navigateTo(paths.AccessDenied));
    }
  }, [canSeePage, dispatch, session]);

  useEffect(() => {
    const token = session?.token || session?.userToken;
    if (token && isMatriculaUser) {
      const payload = getJWTPayload(token);
      if (payload && payload.email) {
        dispatch(getWtsEmployeeByEmail(payload.email));
      }
    }
  }, [session, dispatch, isMatriculaUser]);

  useEffect(() => {
    if (isMatriculaUser && employeeSelected?.ruc) {
      setFilters((f) => ({...f, 'dealer': employeeSelected.ruc}));
    }
  }, [isMatriculaUser, employeeSelected]);

  const handleClearFilters = () => {
    setFilters({
      'status': null,
      'instructor': null,
      'dealer': isMatriculaUser ? employeeSelected?.ruc : null,
      'course': null,
    });
    setSelectedCourseId(null);
    setSearchTerm('');
  };

  const handleEditCourse = (course) => {
    const areaId = areaSelected?.id || areaSelected?.value;
    if (areaId && coursesRaw.length === 0) {
      dispatch(getWtsCourses(areaId));
    }
    setCourseToEdit({...course});
    setModalType('edit');
  };

  const handleViewCourse = (course) => {
    const areaId = areaSelected?.id || areaSelected?.value;
    if (areaId && coursesRaw.length === 0) {
      dispatch(getWtsCourses(areaId));
    }
    setCourseToEdit({...course});
    setModalType('view');
  };

  const handleViewInscritos = (course) => {
    const areaId = areaSelected?.id || areaSelected?.value;
    if (areaId && coursesRaw.length === 0) {
      dispatch(getWtsCourses(areaId));
    }
    setCourseToEdit({...course});
    setModalType('view_inscritos');
  };

  const handleOpenNewModal = () => {
    setCourseToEdit(null);
    setModalType('edit');
  };

  useEffect(() => {
    if (canSeePage) {
      dispatch(getWtsAreas());
      dispatch(getWtsCoursesStatus());
      dispatch(getWtsEmployeesByCharge('INSTRUCTOR'));
    }
  }, [dispatch, canSeePage]);

  useEffect(() => {
    const id = areaSelected?.id || areaSelected?.value;
    if (id) {
      dispatch(getWtsDealer(id));
      dispatch(getWtsCourses(id));
    }
  }, [dispatch, areaSelected]);

  useEffect(() => {
    const id = areaSelected?.id || areaSelected?.value;
    if (id) {
      dispatch(getWtsAgendas(id, currentYear));
      handleClearFilters();
    }
  }, [dispatch, areaSelected, currentYear]);

  useEffect(() => {
    let timer;
    if (agendas.length > 0) {
      setShowMessage(true);
      timer = setTimeout(() => setShowMessage(false), 3000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [agendas]);

  const statusDict = useMemo(() => (Array.isArray(statusRaw) ? statusRaw : []).map((item, i) => ({
    ...item, 'hex': COLORS[i % COLORS.length],
  })), [statusRaw]);

  const filteredAgendas = useMemo(() => {
    const list = Array.isArray(agendas) ? agendas : [];
    return list.filter((item, idx, self) => self.findIndex((t) => t.id === item.id) === idx).filter((ag) => {
      const mDate = moment(ag.start_date || ag.desde);
      return mDate.isValid() && mDate.year() === currentYear &&
        (!filters.status || ag.status === filters.status) &&
        (!filters.instructor || ag.capacitadores?.some((c) =>
          Number(c.employee_code) === Number(filters.instructor))) &&
        (!filters.dealer || ag.concesionarios?.some((d) => d.ruc === filters.dealer && Number(d.participantes) > 0)) &&
        (!filters.course || Number(ag.course_id) === Number(filters.course)) &&
        (ag.nombre_curso || '').toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [agendas, filters, searchTerm, currentYear]);

  const meses = useMemo(() => Array.from({'length': 12}, (_, i) => {
    const d = moment([currentYear, i]);
    return {
      'name': d.format('MMMM').toUpperCase(),
      'monthIndex': i,
      'daysInMonth': d.daysInMonth(),
      'firstDay': d.startOf('month').day(),
      'year': currentYear,
    };
  }), [currentYear]);

  if (!canSeePage) {
    return null;
  }

  return (
    <Container fluid>
      {areaSelected && showMessage && (
        <Message
          success={filteredAgendas.length > 0}
          warning={filteredAgendas.length === 0}
          onDismiss={() => setShowMessage(false)}
          header={filteredAgendas.length > 0 ? 'Datos' : 'Sin datos'}
          content={`Se encontraron ${filteredAgendas.length} cursos.`}
        />
      )}

      <CalendarHeader
        areas={areas}
        areaSelected={areaSelected}
        onAreaChange={(v) => dispatch(changeWtsAreaSelected(v))}
        selectedCourseId={selectedCourseId}
        onClearSelection={() => setSelectedCourseId(null)}
        courseToEdit={courseToEdit}
        isModalOpen={!!modalType}
        onCloseModal={() => setModalType(null)}
        onOpenModal={canEdit ? handleOpenNewModal : null}
      />

      {modalType === 'edit' && canEdit && (
        <WtsAgendaModal
          open={modalType === 'edit'}
          onClose={() => setModalType(null)}
          courseToEdit={courseToEdit}
          reduxData={{
            'instructors': employees,
            'courses': coursesRaw,
            'wtsStatus': statusRaw,
            'areaSelected': areaSelected,
            'wtsDealers': dealersRaw,
            'agendas': agendas,
          }}
        />
      )}

      {modalType === 'view' && canViewMatricula && (
        <WtsAgendaViewOnly
          open={modalType === 'view'}
          onClose={() => setModalType(null)}
          courseToEdit={courseToEdit}
          reduxData={{
            'instructors': employees,
            'courses': coursesRaw,
            'wtsStatus': statusRaw,
            'areaSelected': areaSelected,
            'wtsDealers': dealersRaw,
            'agendas': agendas,
          }}
        />
      )}

      {modalType === 'view_inscritos' && canEdit && (
        <WtsMatriculaIndex
          open={modalType === 'view_inscritos'}
          onClose={() => setModalType(null)}
          courseToEdit={courseToEdit}
          reduxData={{
            'instructors': employees,
            'courses': coursesRaw,
            'wtsStatus': statusRaw,
            'areaSelected': areaSelected,
            'wtsDealers': dealersRaw,
            'agendas': agendas,
          }}
        />
      )}

      <Grid columns="equal" stretched>
        <Grid.Row>
          <Grid.Column width={3}>
            <CalendarFilters
              areaId={areaSelected?.id || areaSelected?.value}
              currentYear={currentYear}
              setCurrentYear={setCurrentYear}
              totalCount={filteredAgendas.length}
              yearOptions={Array.from({'length': 11}, (v, i) => ({
                'key': currentYear - 5 + i,
                'text': (currentYear - 5 + i).toString(),
                'value': currentYear - 5 + i,
              }))}
              filters={filters}
              onFilterChange={(k, v) => setFilters({...filters, [k]: v})}
              onClear={handleClearFilters}
              selectedData={agendas.find((ag) => Number(ag.id) === Number(selectedCourseId))}
              dealers={dealersRaw}
              isDealerDisabled={isMatriculaUser}
              options={{
                'instructors': employees.filter((e) => e.status === 'ACTIVO').map((e) => ({
                  'key': e.id, 'text': e.fullName || `${e.names} ${e.lastNames}`, 'value': e.id,
                })),
                'dealers': dealersRaw.filter((d) => d.status === 'ACTIVO').map((d) => ({
                  'key': d.ruc, 'text': d.description, 'value': d.ruc,
                })),
                'courses': coursesRaw.map((c) => ({
                  'key': c.id, 'text': c.course, 'value': c.id,
                })),
              }}
            />
          </Grid.Column>
          <Grid.Column width={9}>
            <CalendarGrid
              months={meses}
              filteredAgendas={filteredAgendas}
              selectedCourseId={selectedCourseId}
              onCourseSelect={setSelectedCourseId}
              statusDict={statusDict}
            />
          </Grid.Column>
          <Grid.Column width={4}>
            <CalendarSidebar
              statusDict={statusDict}
              filterStatus={filters.status}
              onStatusClick={(v) => setFilters({...filters, 'status': filters.status === v ? null : v})}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              agendas={filteredAgendas}
              selectedId={selectedCourseId}
              onSelect={setSelectedCourseId}
              onEdit={canEdit ? handleEditCourse : null}
              onView={canViewMatricula ? handleViewCourse : null}
              onViewInscritos={canEdit ? handleViewInscritos : null}
              canEdit={canEdit}
              isMatriculaUser={isMatriculaUser}
              canViewMatricula={canViewMatricula}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Container>
  );
};

export default WtsCalendario;
